import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import { supabase, deleteListing } from '../lib/supabase';

const ADMIN_EMAIL = 'kevinman1@gmail.com';

const STATUS_LABELS = { available: 'Available', deposit_paid: 'Deposit Paid', sold: 'Sold' };

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [listingFilter, setListingFilter] = useState('all');
  const [userSearch, setUserSearch] = useState('');

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    if (user.email !== ADMIN_EMAIL) { navigate('/'); return; }
    loadAll();
  }, [user]); // eslint-disable-line

  const loadAll = async () => {
    setLoading(true);
    setError('');
    try {
      const [profilesRes, listingsRes] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, email, created_at')
          .order('created_at', { ascending: false }),
        supabase
          .from('listings')
          .select('id, title, price, status, hidden, created_at, user_id, profiles(username, email)')
          .order('created_at', { ascending: false }),
      ]);
      if (profilesRes.error) throw profilesRes.error;
      if (listingsRes.error) throw listingsRes.error;
      setUsers(profilesRes.data || []);
      setListings(listingsRes.data || []);
    } catch (e) {
      setError('Failed to load data: ' + e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (id, title) => {
    if (!window.confirm(`Delete "${title}" permanently? This cannot be undone.`)) return;
    try {
      await deleteListing(id);
      setListings(prev => prev.filter(l => l.id !== id));
    } catch (e) {
      alert('Delete failed: ' + e.message + '\n\nYou may need to add an admin RLS policy in Supabase to allow deleting other users\' listings.');
    }
  };

  const handleDeleteUser = async (u) => {
    if (u.email === ADMIN_EMAIL) return;
    if (!window.confirm(
      `Remove user "${u.username}"?\n\nThis will delete all their listings and profile data.\n\nNote: To fully remove their login, you must also delete the auth user from the Supabase dashboard.`
    )) return;
    try {
      const { error: le } = await supabase.from('listings').delete().eq('user_id', u.id);
      if (le) throw le;
      const { error: pe } = await supabase.from('profiles').delete().eq('id', u.id);
      if (pe) throw pe;
      setUsers(prev => prev.filter(p => p.id !== u.id));
      setListings(prev => prev.filter(l => l.user_id !== u.id));
    } catch (e) {
      alert('Delete failed: ' + e.message + '\n\nYou may need to add admin RLS policies in Supabase.');
    }
  };

  if (!user || user.email !== ADMIN_EMAIL) return null;

  if (loading) return <div className="spinner-wrap"><div className="spinner" /></div>;

  const filteredListings = listings.filter(l => {
    if (listingFilter === 'hidden') return l.hidden;
    if (listingFilter === 'visible') return !l.hidden;
    if (listingFilter === 'available') return l.status === 'available' && !l.hidden;
    if (listingFilter === 'sold') return l.status === 'sold';
    return true;
  });

  const filteredUsers = users.filter(u =>
    !userSearch || u.username?.toLowerCase().includes(userSearch.toLowerCase()) || u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  const listingCountByUser = {};
  listings.forEach(l => { listingCountByUser[l.user_id] = (listingCountByUser[l.user_id] || 0) + 1; });

  const thStyle = { padding: '.6rem 1rem', textAlign: 'left', fontWeight: 600, fontSize: '.72rem', textTransform: 'uppercase', letterSpacing: '.4px', color: 'var(--ink-light)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const tdStyle = (i) => ({ padding: '.6rem 1rem', borderBottom: '1px solid var(--border)', background: i % 2 === 0 ? 'white' : 'var(--warm-white)', fontSize: '.875rem' });

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <h1 className="page-title">Admin Panel</h1>
            <p className="page-sub">{users.length} users · {listings.length} listings total</p>
          </div>
          <button className="btn btn-g btn-sm" onClick={loadAll}>↻ Refresh</button>
        </div>
      </div>

      {error && <div className="alert alert-e" style={{ marginBottom: '1rem' }}>{error}</div>}

      <div className="alert alert-i" style={{ marginBottom: '1.5rem', fontSize: '.82rem' }}>
        <strong>Note:</strong> Deleting a user removes their profile and listings but not their Supabase auth account.
        To fully remove login access, delete the auth user from the <strong>Supabase Dashboard → Authentication → Users</strong>.
        If delete operations fail with a permission error, add an admin RLS policy in Supabase.
      </div>

      <div className="tabs">
        <div className={`tab ${tab === 'users' ? 'on' : ''}`} onClick={() => setTab('users')}>
          Users ({users.length})
        </div>
        <div className={`tab ${tab === 'listings' ? 'on' : ''}`} onClick={() => setTab('listings')}>
          All Listings ({listings.length})
        </div>
      </div>

      {/* ── USERS TAB ── */}
      {tab === 'users' && (
        <div>
          <div style={{ marginBottom: '1rem' }}>
            <input
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              placeholder="Search by username or email…"
              style={{ padding: '.6rem .9rem', border: '1.5px solid var(--border)', borderRadius: 'var(--r)', fontSize: '.875rem', width: '100%', maxWidth: 320 }}
            />
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 560 }}>
              <thead>
                <tr style={{ background: 'var(--cream)' }}>
                  <th style={thStyle}>Username</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Listings</th>
                  <th style={thStyle}>Joined</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.length === 0 ? (
                  <tr><td colSpan={5} style={{ padding: '2rem', textAlign: 'center', color: 'var(--brown)' }}>No users found</td></tr>
                ) : filteredUsers.map((u, i) => (
                  <tr key={u.id}>
                    <td style={tdStyle(i)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
                        <span
                          style={{ color: 'var(--terracotta)', fontWeight: 600, cursor: 'pointer' }}
                          onClick={() => navigate(`/seller/${u.username}`)}
                        >{u.username}</span>
                        {u.email === ADMIN_EMAIL && (
                          <span style={{ fontSize: '.65rem', background: 'var(--terracotta)', color: '#fff', padding: '.1rem .45rem', borderRadius: 10, fontWeight: 700 }}>ADMIN</span>
                        )}
                      </div>
                    </td>
                    <td style={{ ...tdStyle(i), color: 'var(--brown)' }}>{u.email}</td>
                    <td style={{ ...tdStyle(i), color: 'var(--brown)' }}>{listingCountByUser[u.id] || 0}</td>
                    <td style={{ ...tdStyle(i), color: 'var(--brown)' }}>{format(new Date(u.created_at), 'MMM d, yyyy')}</td>
                    <td style={tdStyle(i)}>
                      {u.email !== ADMIN_EMAIL && (
                        <button className="btn btn-d btn-sm" onClick={() => handleDeleteUser(u)}>Delete User</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── LISTINGS TAB ── */}
      {tab === 'listings' && (
        <div>
          <div className="filter-bar" style={{ marginBottom: '1rem' }}>
            {[
              { v: 'all', l: `All (${listings.length})` },
              { v: 'visible', l: `Visible (${listings.filter(l => !l.hidden).length})` },
              { v: 'hidden', l: `Hidden (${listings.filter(l => l.hidden).length})` },
              { v: 'available', l: 'Available' },
              { v: 'sold', l: 'Sold' },
            ].map(f => (
              <button key={f.v} className={`chip ${listingFilter === f.v ? 'on' : ''}`} onClick={() => setListingFilter(f.v)}>{f.l}</button>
            ))}
          </div>

          <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
              <thead>
                <tr style={{ background: 'var(--cream)' }}>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Seller</th>
                  <th style={thStyle}>Price</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Posted</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {filteredListings.length === 0 ? (
                  <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: 'var(--brown)' }}>No listings found</td></tr>
                ) : filteredListings.map((l, i) => (
                  <tr key={l.id}>
                    <td style={tdStyle(i)}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', flexWrap: 'wrap' }}>
                        <span
                          style={{ color: 'var(--terracotta)', fontWeight: 500, cursor: 'pointer' }}
                          onClick={() => navigate(`/listing/${l.id}`)}
                        >{l.title}</span>
                        {l.hidden && (
                          <span style={{ fontSize: '.65rem', background: '#EEE', color: '#888', padding: '.1rem .4rem', borderRadius: 10, fontWeight: 700, textTransform: 'uppercase' }}>Hidden</span>
                        )}
                      </div>
                    </td>
                    <td style={tdStyle(i)}>
                      <span
                        style={{ color: 'var(--terracotta)', cursor: 'pointer' }}
                        onClick={() => navigate(`/seller/${l.profiles?.username}`)}
                      >{l.profiles?.username || '—'}</span>
                    </td>
                    <td style={{ ...tdStyle(i), color: 'var(--terracotta)', fontWeight: 600 }}>${Number(l.price).toFixed(2)}</td>
                    <td style={tdStyle(i)}><span className={`badge b-${l.status}`}>{STATUS_LABELS[l.status] || l.status}</span></td>
                    <td style={{ ...tdStyle(i), color: 'var(--brown)', whiteSpace: 'nowrap' }}>{format(new Date(l.created_at), 'MMM d, yyyy')}</td>
                    <td style={tdStyle(i)}>
                      <button className="btn btn-d btn-sm" onClick={() => handleDeleteListing(l.id, l.title)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
